import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, Navigation, X, Car, Bus, Footprints } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = '/api';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showDirections, setShowDirections] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Sporočilo uspešno poslano!', {
        description: 'Odgovorili vam bomo v najkrajšem možnem času.',
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Napaka pri pošiljanju', {
        description: 'Prosimo, poskusite ponovno.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="section-padding bg-slate-50"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-4">
            Kontakt
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-6">
            Stopite v stik z nami
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Imate vprašanja? Z veseljem vam odgovorimo. Pokličite nas ali izpolnite obrazec.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            {/* Mobile: Compact horizontal layout, Desktop: 2x2 grid */}
            <div className="hidden sm:grid sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="contact-info-0">
                <div className="w-12 h-12 bg-[#f87171]/10 rounded-xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-[#f87171]" />
                </div>
                <h4 className="font-semibold text-[#1a365d] mb-2">Lokacija</h4>
                <p className="text-slate-600 text-sm">DoubleTree by Hilton Ljubljana</p>
                <p className="text-slate-600 text-sm">Dunajska c. 154</p>
                <p className="text-slate-600 text-sm">1000 Ljubljana</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="contact-info-1">
                <div className="w-12 h-12 bg-[#f87171]/10 rounded-xl flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-[#f87171]" />
                </div>
                <h4 className="font-semibold text-[#1a365d] mb-2">Telefon</h4>
                <a href="tel:+38640464886" className="text-slate-600 text-sm hover:text-[#f87171] transition-colors">
                  +386 40 464 886
                </a>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="contact-info-2">
                <div className="w-12 h-12 bg-[#f87171]/10 rounded-xl flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-[#f87171]" />
                </div>
                <h4 className="font-semibold text-[#1a365d] mb-2">E-pošta</h4>
                <a href="mailto:fizioterapija@sasapanic.com" className="text-slate-600 text-sm hover:text-[#f87171] transition-colors">
                  fizioterapija@sasapanic.com
                </a>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="contact-info-3">
                <div className="w-12 h-12 bg-[#f87171]/10 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-[#f87171]" />
                </div>
                <h4 className="font-semibold text-[#1a365d] mb-2">Delovni čas</h4>
                <p className="text-slate-600 text-sm">Pon - Pet: 8:00 - 19:00</p>
                <p className="text-slate-600 text-sm">Sob: po dogovoru</p>
              </div>
            </div>

            {/* Mobile: Compact list layout */}
            <div className="sm:hidden bg-white rounded-2xl shadow-sm mb-6 divide-y divide-slate-100" data-testid="contact-info-mobile">
              <a href="https://maps.app.goo.gl/LMh3RZepmt6NsNaL8" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                <div className="w-10 h-10 bg-[#f87171]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#f87171]" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-[#1a365d] text-sm">Lokacija</h4>
                  <p className="text-slate-500 text-xs truncate">DoubleTree by Hilton, Dunajska c. 154</p>
                </div>
              </a>
              
              <a href="tel:+38640464886" className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                <div className="w-10 h-10 bg-[#f87171]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#f87171]" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-[#1a365d] text-sm">Telefon</h4>
                  <p className="text-slate-500 text-xs">+386 40 464 886</p>
                </div>
              </a>
              
              <a href="mailto:fizioterapija@sasapanic.com" className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                <div className="w-10 h-10 bg-[#f87171]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#f87171]" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-[#1a365d] text-sm">E-pošta</h4>
                  <p className="text-slate-500 text-xs truncate">fizioterapija@sasapanic.com</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 bg-[#f87171]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#f87171]" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-[#1a365d] text-sm">Delovni čas</h4>
                  <p className="text-slate-500 text-xs">Pon-Pet: 8-19h • Sob: 8-13h</p>
                </div>
              </div>
            </div>

            {/* How to Get Here Button */}
            <Button
              onClick={() => {
                setShowDirections(true);
                window.history.pushState({}, '', '/fizioterapija-sasa-panic-lokacija');
              }}
              className="w-full mb-4 bg-[#1a365d] hover:bg-[#1a365d]/90 text-white rounded-xl py-3 font-semibold"
              data-testid="how-to-get-here-btn"
            >
              <Navigation className="w-5 h-5 mr-2" />
              Kako do nas?
            </Button>

            <div className="map-container h-80 rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://maps.google.com/maps?q=Fizioterapija%20Sa%C5%A1a%20Pani%C4%8D%2C%20s.p.%2C%20Dunajska%20c.%20154%2C%201000%20Ljubljana&ftid=0x4765338d7f7690cd:0x6d1201c3c2b8a5a8&z=16&hl=sl&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lokacija fizioterapije Saša Panič - Dunajska c. 154, Ljubljana"
              />
            </div>

            {/* Directions Modal */}
            {showDirections && (
              <div 
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                onClick={() => {
                  setShowDirections(false);
                  window.history.pushState({}, '', '/');
                }}
                data-testid="directions-modal-overlay"
              >
                <div 
                  className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="directions-modal"
                >
                  <div className="sticky top-0 bg-[#1a365d] text-white p-4 rounded-t-2xl flex items-center justify-between">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Navigation className="w-5 h-5" />
                      Kako do nas?
                    </h3>
                    <button 
                      onClick={() => {
                        setShowDirections(false);
                        window.history.pushState({}, '', '/');
                      }}
                      className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                      data-testid="close-directions-modal"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Address */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-6 h-6 text-[#f87171] flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="font-bold text-[#1a365d]">Naslov</h4>
                          <p className="text-slate-600">DoubleTree by Hilton Ljubljana</p>
                          <p className="text-slate-600">Dunajska c. 154</p>
                          <p className="text-slate-600">1000 Ljubljana, Slovenija</p>
                        </div>
                      </div>
                    </div>

                    {/* By Car */}
                    <div className="border rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Car className="w-5 h-5 text-blue-600" />
                        </div>
                        <h4 className="font-bold text-[#1a365d]">Z avtomobilom</h4>
                      </div>
                      <ul className="space-y-2 text-slate-600 text-sm ml-2">
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></span>
                          <span>Iz avtoceste Ljubljana - Celje/Maribor, izstop <strong>Ljubljana Bežigrad</strong></span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></span>
                          <span>Sledite Dunajski cesti proti centru mesta</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></span>
                          <span>Hotel DoubleTree by Hilton je na desni strani, tik pred WTC</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></span>
                          <span><strong>Parkiranje:</strong> Brezplačno v garažni hiši <a href="https://maps.google.com/?q=Parkirna+hiša+Smelt+WTC,+Dunajska+cesta+158,+Ljubljana" target="_blank" rel="noopener noreferrer" className="text-[#f87171] hover:underline">Smelt/WTC, Dunajska c. 158</a></span>
                        </li>
                      </ul>
                    </div>

                    {/* By Bus */}
                    <div className="border rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <Bus className="w-5 h-5 text-green-600" />
                        </div>
                        <h4 className="font-bold text-[#1a365d]">Z avtobusom (LPP)</h4>
                      </div>
                      <ul className="space-y-2 text-slate-600 text-sm ml-2">
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                          <span>Avtobusna linija <strong>6</strong> (smer Center - Črnuče)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                          <span>Postaja: <strong>WTC</strong> (World Trade Center)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></span>
                          <span>Od avtobusne postaje do hotela: cca 1 min hoje</span>
                        </li>
                      </ul>
                    </div>

                    {/* On Foot / Bike */}
                    <div className="border rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <Footprints className="w-5 h-5 text-amber-600" />
                        </div>
                        <h4 className="font-bold text-[#1a365d]">Peš / S kolesom</h4>
                      </div>
                      <ul className="space-y-2 text-slate-600 text-sm ml-2">
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0"></span>
                          <span>Iz centra Ljubljane: cca <strong>25 min</strong> peš po Dunajski cesti</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0"></span>
                          <span><strong>BicikeLJ</strong> postaja v bližini (Dunajska / WTC)</span>
                        </li>
                      </ul>
                    </div>

                    {/* Google Maps Link */}
                    <a
                      href="https://maps.app.goo.gl/LMh3RZepmt6NsNaL8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-[#f87171] hover:bg-[#f87171]/90 text-white text-center py-3 rounded-xl font-semibold transition-colors"
                      data-testid="open-google-maps"
                    >
                      Odpri v Google Maps
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg" data-testid="contact-form">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#1a365d] mb-4">
                  Hvala za sporočilo!
                </h3>
                <p className="text-slate-600 mb-6">
                  Odgovorili vam bomo v najkrajšem možnem času.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="rounded-full"
                >
                  Pošlji novo sporočilo
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="text-2xl font-bold text-[#1a365d] mb-6">
                  Pošljite nam sporočilo
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1a365d] mb-2">
                      Ime in priimek *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Vaše ime"
                      className="bg-slate-50 border-slate-200 focus:border-[#1a365d] rounded-xl h-12"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1a365d] mb-2">
                      E-pošta *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="vasa@email.si"
                      className="bg-slate-50 border-slate-200 focus:border-[#1a365d] rounded-xl h-12"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a365d] mb-2">
                    Telefon
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+386 40 123 456"
                    className="bg-slate-50 border-slate-200 focus:border-[#1a365d] rounded-xl h-12"
                    data-testid="contact-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a365d] mb-2">
                    Sporočilo *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Opišite vaše težave ali vprašanja..."
                    rows={5}
                    className="bg-slate-50 border-slate-200 focus:border-[#1a365d] rounded-xl resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#f87171] hover:bg-[#f87171]/90 text-white rounded-full py-4 text-lg font-semibold btn-lift"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    'Pošiljam...'
                  ) : (
                    <>
                      Pošlji sporočilo
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
